import { DateTime } from "luxon";
import { CcmsSuspension, PgmProgressStatus } from "@/types";
import { toFormat, calculateTimeRemaining } from "@/lib/dateHelpers";
import { LEVEL_NUMBERS } from "./pgm-levels";

export const SUSPENSION_CODES_WITH_TERMINATION = ["GPTP", "NPDU"] as const;
const TERMINATION_WINDOW_YEARS = 2;
const MAX_LEVEL_NUMERAL = LEVEL_NUMBERS[LEVEL_NUMBERS.length - 1];

type SuspensionCode = "GPTP" | "NPDU" | "COEV";

interface SuspensionBannerConfig {
  description: string;
  deadlineLabel: string | null;
}

export interface SuspensionData {
  code: string;
  startDate: string;
  isActive: boolean;
  hasTerminationWindow: boolean;
  terminationDate: string | null;
  formattedTerminationDate: string | null;
  timeRemaining: string | null;
}

const BANNER_CONFIGS: Record<SuspensionCode, SuspensionBannerConfig> = {
  GPTP: {
    description: "This person is suspended for not meeting acceptable progress requirements",
    deadlineLabel: "Termination Deadline"
  },
  NPDU: {
    description: "This person is suspended due to non-payment of dues",
    deadlineLabel: "Termination Deadline"
  },
  COEV: {
    description: "This person is suspended due to a Code of Ethics violation",
    deadlineLabel: null
  }
};

const SUPPORTED_CODES = new Set<string>(Object.keys(BANNER_CONFIGS));

function isSupportedCode(code: string): code is SuspensionCode {
  return SUPPORTED_CODES.has(code);
}

export function calculateSuspensionData(suspension: CcmsSuspension): SuspensionData | null {
  if (!suspension?.startDate) return null;

  const isActive = !suspension.endDate;
  const hasTermination = (SUSPENSION_CODES_WITH_TERMINATION as readonly string[]).includes(suspension.code);

  let terminationDate: string | null = null;
  let formattedTerminationDate: string | null = null;
  let timeRemaining: string | null = null;

  if (hasTermination && isActive) {
    const termDate = DateTime.fromISO(suspension.startDate).plus({ years: TERMINATION_WINDOW_YEARS });
    if (termDate.isValid) {
      terminationDate = termDate.toISODate();
      formattedTerminationDate = toFormat(terminationDate!);
      timeRemaining = calculateTimeRemaining(terminationDate);
    }
  }

  return {
    code: suspension.code,
    startDate: suspension.startDate,
    isActive,
    hasTerminationWindow: hasTermination,
    terminationDate,
    formattedTerminationDate,
    timeRemaining
  };
}

export function getActiveSuspensionFromProgress(pgmProgress: PgmProgressStatus): SuspensionData | null {
  const suspensions = pgmProgress.suspensions;
  if (!suspensions || suspensions.length === 0) return null;

  const activeSuspensions = suspensions
    .map((s) => calculateSuspensionData(s))
    .filter((d): d is SuspensionData => d !== null && d.isActive && isSupportedCode(d.code));

  if (activeSuspensions.length === 0) return null;

  const withTermination = activeSuspensions
    .filter((d) => d.hasTerminationWindow && d.terminationDate)
    .sort((a, b) => new Date(a.terminationDate!).getTime() - new Date(b.terminationDate!).getTime());

  if (withTermination.length > 0) return withTermination[0];

  return activeSuspensions[0];
}

export function getSuspensionBannerConfig(code: string): SuspensionBannerConfig | null {
  if (!isSupportedCode(code)) return null;
  return BANNER_CONFIGS[code];
}

export function formatTerminationDeadline(suspensionData: SuspensionData): string | null {
  if (!suspensionData.formattedTerminationDate) return null;

  const config = getSuspensionBannerConfig(suspensionData.code);
  if (!config?.deadlineLabel) return null;

  const remaining = suspensionData.timeRemaining ? ` (${suspensionData.timeRemaining} remaining)` : "";
  return `${config.deadlineLabel}: ${suspensionData.formattedTerminationDate}${remaining}`;
}

export function getGptpProgressGuidance(
  suspensionCode: string,
  currentLevelNumeral: number | null | undefined
): string | null {
  if (suspensionCode !== "GPTP" || currentLevelNumeral == null) return null;

  const levelSegment = `All remaining requirements listed under Level ${currentLevelNumeral}`;
  if (currentLevelNumeral >= MAX_LEVEL_NUMERAL) {
    return `${levelSegment} must be completed to get back on track.`;
  }

  return `${levelSegment} and purchase of Level ${currentLevelNumeral + 1} must be completed to get back on track.`;
}

export const ACCEPTABLE_PROGRESS_TERMINATION_BANNER_DESCRIPTION =
  "This person is terminated for failing to meet acceptable progress requirements";

export function getAcceptableProgressTerminationGuidance(
  currentLevelNumeral: number | null | undefined,
  patMustBeRetaken = false
): string {
  const guidance =
    currentLevelNumeral == null || currentLevelNumeral < 1
      ? "They can still complete remaining current-level requirements and reapply. The next level cannot be purchased until reapplication is approved."
      : currentLevelNumeral >= MAX_LEVEL_NUMERAL
        ? `They can still complete remaining Level ${currentLevelNumeral} requirements and reapply.`
        : `They can still complete remaining Level ${currentLevelNumeral} requirements and reapply. Level ${currentLevelNumeral + 1} cannot be purchased until reapplication is approved.`;

  if (!patMustBeRetaken) return guidance;

  return `${guidance} Their Playing Ability Test has expired and must be retaken before they can reapply.`;
}

export function getCurrentLevelNumeral(pgmProgress: PgmProgressStatus | null | undefined): number | null {
  const match = pgmProgress?.currentLevelKey?.match(/^level_?(\d+)$/);
  const numeral = match ? Number(match[1]) : NaN;
  return Number.isFinite(numeral) ? numeral : null;
}

/**
 * Returns the level numeral that was active when the given suspension started.
 *
 * The banner guidance ("Level X requirements and purchase of Level X+1") describes
 * the work that needs to happen to clear an existing GPTP suspension. The suspension
 * is opened against whatever level the person was enrolled in at the time, so the
 * guidance must stay anchored to that level — not to `currentLevelKey`, which can
 * advance the moment the next level is purchased (before the nightly job clears
 * the suspension in CCMS). Without this anchoring, purchasing Level N+1 makes the
 * banner misleadingly start telling the person to also purchase Level N+2.
 *
 * Resolution: pick the level whose `startDate` is the latest one on or before the
 * suspension's `startDate`. Falls back to `getCurrentLevelNumeral` only if no
 * level history is usable.
 */
export function getLevelNumeralAtSuspensionStart(
  pgmProgress: PgmProgressStatus | null | undefined,
  suspension: { startDate?: string | null } | null | undefined
): number | null {
  if (!suspension?.startDate) return getCurrentLevelNumeral(pgmProgress);

  const suspensionStartMs = new Date(suspension.startDate).getTime();
  if (!Number.isFinite(suspensionStartMs)) return getCurrentLevelNumeral(pgmProgress);

  const candidates = (pgmProgress?.levels ?? [])
    .filter((level) => Number.isFinite(level?.numeral) && level.numeral > 0 && level.startDate)
    .map((level) => ({ numeral: level.numeral, startMs: new Date(level.startDate as string).getTime() }))
    .filter((entry) => Number.isFinite(entry.startMs) && entry.startMs <= suspensionStartMs)
    .sort((a, b) => b.startMs - a.startMs);

  return candidates.length > 0 ? candidates[0].numeral : getCurrentLevelNumeral(pgmProgress);
}
